import SearchableSelectCustom from './SearchableSelectCustom'
import { SelectButton } from './select/SelectButton'
import { SelectListboxShell } from './select/SelectListboxShell'
import { SelectOptionsList } from './select/SelectOptionsList'
import { findSelectedOption } from './select/text'
import type { SelectOption, SelectSize } from './select/types'
import { useSelectTypeahead } from './useSelectTypeahead'

export type { SelectOption } from './select/types'

/**
 * Props for the shared select component. Set `searchable` to delegate to the
 * searchable variant while preserving the same controlled value contract.
 */
export type SelectCustomProps = {
  /** Current selected option value. */
  value: string
  /** Options shown in the dropdown. */
  options: SelectOption[]
  /** Called when the selected option value changes. */
  onChange: (value: string) => void
  /** Called when focus leaves the control. */
  onBlur?: () => void
  buttonId?: string
  ariaLabel?: string
  ariaInvalid?: boolean
  describedById?: string
  placeholder?: string
  disabled?: boolean
  size?: SelectSize
  emphasizePrimaryLabel?: boolean
  buttonClassName?: string
  optionsClassName?: string
  searchable?: boolean
  searchPlaceholder?: string
  emptyLabel?: string
}

type PlainSelectCustomProps = Omit<SelectCustomProps, 'searchable' | 'searchPlaceholder' | 'emptyLabel'>

const plainDropdownPositionConfig = {
  widthStrategy: 'match-options',
  minMeasuredHeight: 1,
  minMaxHeight: 160,
} as const

const PlainSelectCustom = ({
  value,
  options,
  onChange,
  onBlur,
  buttonId,
  ariaLabel,
  ariaInvalid = false,
  describedById,
  placeholder = '',
  disabled = false,
  size = 'md',
  emphasizePrimaryLabel = false,
  buttonClassName,
  optionsClassName,
}: PlainSelectCustomProps) => {
  const selectedOption = findSelectedOption(options, value)
  const selectedLabel = selectedOption?.label ?? placeholder
  const handleTypeahead = useSelectTypeahead({ options, value, onChange })

  return (
    <SelectListboxShell
      value={value}
      onChange={onChange}
      disabled={disabled}
      dropdownPositionConfig={plainDropdownPositionConfig}
      renderButton={({ buttonRef }) => (
        <SelectButton
          buttonRef={buttonRef}
          buttonId={buttonId}
          ariaLabel={ariaLabel}
          ariaInvalid={ariaInvalid}
          describedById={describedById}
          onBlur={onBlur}
          onKeyDown={(event) => {
            if (event.metaKey || event.ctrlKey || event.altKey) return
            handleTypeahead(event.key)
          }}
          valueLabel={selectedLabel}
          selectedOption={selectedOption}
          size={size}
          emphasizePrimaryLabel={emphasizePrimaryLabel}
          buttonClassName={buttonClassName}
          chevronClassName="text-slate-400"
        />
      )}
      renderOptions={({ optionsRef, placement, style }) => (
        <SelectOptionsList
          options={options}
          emphasizePrimaryLabel={emphasizePrimaryLabel}
          optionsClassName={optionsClassName}
          placement={placement}
          style={style}
          optionsRef={optionsRef}
        />
      )}
    />
  )
}

const SelectCustom = ({
  searchable = false,
  searchPlaceholder = 'Search',
  emptyLabel = 'No results',
  ...props
}: SelectCustomProps) => {
  if (searchable) {
    return (
      <SearchableSelectCustom
        {...props}
        searchPlaceholder={searchPlaceholder}
        emptyLabel={emptyLabel}
      />
    )
  }

  return <PlainSelectCustom {...props} />
}

export default SelectCustom
